/**
 * Tier 3 dynamic-block OBJECTS writers — DYN-3 Batch D.
 *
 * Symmetric to `dxfBridge.dynamic.parser.tier3.ts`. Emits one
 * `DxfDynamicBlockObject` per Tier 3 parameter / action / geometric
 * constraint.
 *
 * Group-code conventions match the parser — keep in sync.
 */
import type { DxfDynamicBlockObject, DxfGroup } from "@dxf-ai-editor/dxf-io";

import type {
  AlignedConstraintParameter,
  AlignmentParameter,
  DiameterConstraintParameter,
  DynamicAction,
  DynamicParameter,
  GeometricConstraint,
  GeometricConstraintKind,
  PolarStretchAction,
  RadialConstraintParameter,
  ValueSet,
} from "./dynamicBlock";
import { pushGroup } from "./dxfBridge.dynamic.shared";

// ═══════════════════════════════════════════════════════════════════════════
// Writer context (matches Tier 2)
// ═══════════════════════════════════════════════════════════════════════════

export type Tier3WriterContext = {
  nextHandle: () => string;
  blockRecordHandle: string;
};

// ═══════════════════════════════════════════════════════════════════════════
// Shared helpers (duplicated from tier2 to avoid cross-tier coupling)
// ═══════════════════════════════════════════════════════════════════════════

type AxisValueSetCodes = {
  modeCode: number;
  incrementCode: number;
  minCode: number;
  maxCode: number;
  listCode: number;
};

function emitAxisValueSet(
  groups: DxfGroup[],
  valueSet: ValueSet,
  codes: AxisValueSetCodes,
): void {
  const mode =
    valueSet.mode === "increment" ? 1 : valueSet.mode === "list" ? 2 : 0;
  pushGroup(groups, codes.modeCode, mode);
  pushGroup(groups, codes.incrementCode, valueSet.increment);
  if (valueSet.min !== undefined) pushGroup(groups, codes.minCode, valueSet.min);
  if (valueSet.max !== undefined) pushGroup(groups, codes.maxCode, valueSet.max);
  for (const v of valueSet.listValues) {
    pushGroup(groups, codes.listCode, v);
  }
}

function emitBaseParamHeader(
  groups: DxfGroup[],
  className: string,
  handle: string,
  ownerHandle: string,
  subclasses: string[],
  name: string,
  label: string,
  description: string,
): void {
  pushGroup(groups, 0, className);
  pushGroup(groups, 5, handle);
  pushGroup(groups, 330, ownerHandle);
  for (const sub of subclasses) pushGroup(groups, 100, sub);
  pushGroup(groups, 1, name);
  if (label) pushGroup(groups, 3, label);
  if (description) pushGroup(groups, 4, description);
}

function emitBaseFlags(
  groups: DxfGroup[],
  chainActions: boolean,
  showGrip: boolean,
): void {
  pushGroup(groups, 280, chainActions ? 1 : 0);
  pushGroup(groups, 281, showGrip ? 1 : 0);
}

// ═══════════════════════════════════════════════════════════════════════════
// Tier 3 parameters
// ═══════════════════════════════════════════════════════════════════════════

function writeAlignmentParameter(
  p: AlignmentParameter,
  ctx: Tier3WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = p.id || ctx.nextHandle();
  emitBaseParamHeader(
    groups,
    "ACDBBLOCKALIGNMENTPARAMETER",
    handle,
    ctx.blockRecordHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockParameter",
      "AcDbBlock1PtParameter",
      "AcDbBlockAlignmentParameter",
    ],
    p.name,
    p.label,
    p.description,
  );
  pushGroup(groups, 10, p.position.x);
  pushGroup(groups, 20, p.position.y);
  pushGroup(groups, 30, 0);
  pushGroup(groups, 50, p.defaultAngle);
  pushGroup(groups, 51, p.currentAngle);
  pushGroup(groups, 70, p.alignmentType === "parallel" ? 1 : 0);
  emitBaseFlags(groups, p.chainActions, p.showGrip);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKALIGNMENTPARAMETER",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

function writeAlignedConstraintParameter(
  p: AlignedConstraintParameter,
  ctx: Tier3WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = p.id || ctx.nextHandle();
  emitBaseParamHeader(
    groups,
    "ACDBBLOCKCONSTRAINEDLINEARLENGTH",
    handle,
    ctx.blockRecordHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockParameter",
      "AcDbBlock2PtParameter",
      "AcDbBlockConstraintParameter",
      "AcDbBlockAlignedConstraintParameter",
    ],
    p.name,
    p.label,
    p.description,
  );
  pushGroup(groups, 10, p.pointA.x);
  pushGroup(groups, 20, p.pointA.y);
  pushGroup(groups, 30, 0);
  pushGroup(groups, 11, p.pointB.x);
  pushGroup(groups, 21, p.pointB.y);
  pushGroup(groups, 31, 0);
  pushGroup(groups, 140, p.defaultDistance);
  pushGroup(groups, 141, p.currentDistance);
  emitAxisValueSet(groups, p.distanceValueSet, {
    modeCode: 170,
    incrementCode: 142,
    minCode: 143,
    maxCode: 144,
    listCode: 145,
  });
  emitBaseFlags(groups, p.chainActions, p.showGrip);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKCONSTRAINEDLINEARLENGTH",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

function writeRadialConstraintParameter(
  p: RadialConstraintParameter,
  ctx: Tier3WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = p.id || ctx.nextHandle();
  emitBaseParamHeader(
    groups,
    "ACDBBLOCKCONSTRAINEDRADIAL",
    handle,
    ctx.blockRecordHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockParameter",
      "AcDbBlock2PtParameter",
      "AcDbBlockConstraintParameter",
      "AcDbBlockRadialConstraintParameter",
    ],
    p.name,
    p.label,
    p.description,
  );
  pushGroup(groups, 10, p.center.x);
  pushGroup(groups, 20, p.center.y);
  pushGroup(groups, 30, 0);
  pushGroup(groups, 11, p.radiusPoint.x);
  pushGroup(groups, 21, p.radiusPoint.y);
  pushGroup(groups, 31, 0);
  pushGroup(groups, 140, p.defaultRadius);
  pushGroup(groups, 141, p.currentRadius);
  emitAxisValueSet(groups, p.radiusValueSet, {
    modeCode: 170,
    incrementCode: 142,
    minCode: 143,
    maxCode: 144,
    listCode: 145,
  });
  emitBaseFlags(groups, p.chainActions, p.showGrip);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKCONSTRAINEDRADIAL",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

function writeDiameterConstraintParameter(
  p: DiameterConstraintParameter,
  ctx: Tier3WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = p.id || ctx.nextHandle();
  emitBaseParamHeader(
    groups,
    "ACDBBLOCKCONSTRAINEDDIAMETER",
    handle,
    ctx.blockRecordHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockParameter",
      "AcDbBlock3PtParameter",
      "AcDbBlockConstraintParameter",
      "AcDbBlockDiameterConstraintParameter",
    ],
    p.name,
    p.label,
    p.description,
  );
  pushGroup(groups, 10, p.center.x);
  pushGroup(groups, 20, p.center.y);
  pushGroup(groups, 30, 0);
  pushGroup(groups, 11, p.point1.x);
  pushGroup(groups, 21, p.point1.y);
  pushGroup(groups, 31, 0);
  pushGroup(groups, 12, p.point2.x);
  pushGroup(groups, 22, p.point2.y);
  pushGroup(groups, 32, 0);
  pushGroup(groups, 140, p.defaultDiameter);
  pushGroup(groups, 141, p.currentDiameter);
  emitAxisValueSet(groups, p.diameterValueSet, {
    modeCode: 170,
    incrementCode: 142,
    minCode: 143,
    maxCode: 144,
    listCode: 145,
  });
  emitBaseFlags(groups, p.chainActions, p.showGrip);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKCONSTRAINEDDIAMETER",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Tier 3 actions — Polar Stretch
// ═══════════════════════════════════════════════════════════════════════════

function writePolarStretchAction(
  a: PolarStretchAction,
  paramHandle: string,
  ctx: Tier3WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = a.id || ctx.nextHandle();
  pushGroup(groups, 0, "ACDBBLOCKPOLARSTRETCHACTION");
  pushGroup(groups, 5, handle);
  pushGroup(groups, 330, paramHandle);
  pushGroup(groups, 100, "AcDbEvalExpr");
  pushGroup(groups, 100, "AcDbBlockElement");
  pushGroup(groups, 100, "AcDbBlockAction");
  pushGroup(groups, 100, "AcDbBlockActionWithBasePt");
  pushGroup(groups, 100, "AcDbBlockPolarStretchAction");
  if (a.name) pushGroup(groups, 1, a.name);
  pushGroup(groups, 340, paramHandle);
  pushGroup(groups, 91, a.selectionEntityIds.length);
  for (const id of a.selectionEntityIds) pushGroup(groups, 340, id);
  pushGroup(groups, 40, a.distanceMultiplier);
  pushGroup(groups, 41, a.angleOffset);
  pushGroup(groups, 10, a.crossingFrame.corner1.x);
  pushGroup(groups, 20, a.crossingFrame.corner1.y);
  pushGroup(groups, 30, 0);
  pushGroup(groups, 11, a.crossingFrame.corner2.x);
  pushGroup(groups, 21, a.crossingFrame.corner2.y);
  pushGroup(groups, 31, 0);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKPOLARSTRETCHACTION",
    handle,
    ownerHandle: paramHandle,
    rawGroups: groups,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Geometric constraint writer
// ═══════════════════════════════════════════════════════════════════════════

const KIND_TO_SUBCLASS: Record<GeometricConstraintKind, string> = {
  coincident: "AcDb2dCoincidentConstraint",
  collinear: "AcDb2dCollinearConstraint",
  concentric: "AcDb2dConcentricConstraint",
  fix: "AcDb2dFixedConstraint",
  parallel: "AcDb2dParallelConstraint",
  perpendicular: "AcDb2dPerpendicularConstraint",
  tangent: "AcDb2dTangentConstraint",
  equal: "AcDb2dEqualConstraint",
  symmetric: "AcDb2dSymmetricConstraint",
  smooth: "AcDb2dSmoothConstraint",
  horizontal: "AcDb2dHorizontalConstraint",
  vertical: "AcDb2dVerticalConstraint",
};

export function writeGeometricConstraint(
  c: GeometricConstraint,
  ctx: Tier3WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = c.id || ctx.nextHandle();
  pushGroup(groups, 0, "ACDBBLOCK2DGEOMETRICCONSTRAINT");
  pushGroup(groups, 5, handle);
  pushGroup(groups, 330, ctx.blockRecordHandle);
  pushGroup(groups, 100, "AcDbEvalExpr");
  pushGroup(groups, 100, "AcDbBlockElement");
  pushGroup(groups, 100, "AcDbBlock2dConstraint");
  pushGroup(groups, 100, KIND_TO_SUBCLASS[c.kind]);
  for (const eid of c.entityIds) {
    pushGroup(groups, 340, eid);
  }
  if (c.kind === "symmetric" && c.referenceLineId) {
    pushGroup(groups, 341, c.referenceLineId);
  }
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCK2DGEOMETRICCONSTRAINT",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Dispatchers — used by the top-level writer
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Tier 3 parameter dispatcher. Returns `undefined` when the parameter is
 * not Tier 3 (caller should handle it via Tier 1 / 2 emitters).
 */
export function tryWriteTier3Parameter(
  p: DynamicParameter,
  ctx: Tier3WriterContext,
): DxfDynamicBlockObject | undefined {
  switch (p.kind) {
    case "alignment":
      return writeAlignmentParameter(p, ctx);
    case "constraint-aligned":
      return writeAlignedConstraintParameter(p, ctx);
    case "constraint-radial":
      return writeRadialConstraintParameter(p, ctx);
    case "constraint-diameter":
      return writeDiameterConstraintParameter(p, ctx);
    default:
      return undefined;
  }
}

/**
 * Tier 3 action dispatcher. Returns `undefined` when the action is not
 * Tier 3.
 */
export function tryWriteTier3Action(
  a: DynamicAction,
  paramHandle: string,
  ctx: Tier3WriterContext,
): DxfDynamicBlockObject | undefined {
  switch (a.kind) {
    case "polar-stretch":
      return writePolarStretchAction(a, paramHandle, ctx);
    default:
      return undefined;
  }
}
